import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Tabs,
  Tab,
  Table,
  Modal,
  Spinner,
} from "react-bootstrap";
import { FaEye, FaEdit, FaTrash, FaFilePdf } from "react-icons/fa";
import axios from "axios";
import * as XLSX from "xlsx";
import BrainRockLogo from "../../assets/images/brainrock_logo.png";
import ZeeLogo from "../../assets/images/zeepnglogo.jpeg";
import AdminHeader from "./AdminHeader";
import LeftNavManagement from "./LeftNavManagement";

const API_URL = "https://brainrock.in/brainrock/backend/api/employee-profile/";
const DOC_BASE_URL = "https://brainrock.in/brainrock/backend";
const DOC_DELETE_URL =
  "https://brainrock.in/brainrock/backend/api/delete-employee-document/";
const FIRM_OPTIONS = [
  "Brainrock Consulting Services",
  "ZEE - Zero Error Enterprises",
  "Diksha Enterprises",
  "U.S. Infotech",
];

const EmployeeDetailsManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Form state for adding/editing employee
  const [formData, setFormData] = useState({
    emp_name: "",
    education_qualification: "",
    certificate_reward: "",
    designation: "",
    work_experience: "",
    technical_skills: "",
    other_skills: "",
    email: "",
    job_location: "",
    address: "",
    firm_name: "Brainrock Consulting Services",
    govt_doc_type: "aadhaar",
    other_govt_doc_type: "",
  });

  // State for existing file previews in edit mode
  const [existingFiles, setExistingFiles] = useState({
    profile_pic: null,
    govt_document: null,
    educational_documents: [],
    experience_certificates: [],
    professional_certificates: [],
  });

  const [files, setFiles] = useState({
    profile_pic: null,
    govt_document: null,
    educational_documents: [],
    experience_certificates: [],
    professional_certificates: [],
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState("success"); // 'success' or 'danger'
  const [showAlert, setShowAlert] = useState(false);

  // Manage tab state
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("manage");
  const [editMode, setEditMode] = useState(false);
  const [selectedFirmFilter, setSelectedFirmFilter] = useState("all");
  const [showAllEmployeesModal, setShowAllEmployeesModal] = useState(false);
  const [allEmployeesPreviewHtml, setAllEmployeesPreviewHtml] = useState("");

  // Helper functions for document paths and types
  const getDocumentUrl = (docPath) => {
    if (!docPath) return "";
    // Assuming docPath from backend already includes /media/employee_documents/...
    return `${DOC_BASE_URL}${docPath}`;
  };

  const getDocumentName = (docPath) => {
    if (!docPath) return "N/A";
    return docPath.split("/").pop();
  };

  const isImageDocument = (docPath) => {
    if (!docPath) return false;
    return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(docPath);
  };

  const renderDocumentPreviewInModal = (docPath) => {
    if (!docPath) return "N/A";
    const fullUrl = getDocumentUrl(docPath);
    const label = getDocumentName(docPath);
    return isImageDocument(docPath) ? (
      <img src={fullUrl} alt="preview" className="file-preview-image-small" />
    ) : (
      <span>{label}</span>
    );
  };

  // Responsive check
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setSidebarOpen(width >= 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Fetch employees for the "Manage" tab
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL, {
        withCredentials: true,
      });
      if (response.data.success) {
        const data = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setAllEmployees(data);
        setEmployees(data);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setMessage("Failed to fetch employee data.");
      setVariant("danger");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleFileChange = (e) => {
    const { name, files: inputFiles } = e.target;
    if (e.target.multiple) {
      setFiles((prev) => ({ ...prev, [name]: [...inputFiles] }));
    } else {
      setFiles((prev) => ({ ...prev, [name]: inputFiles[0] }));
    }
  };

  const clearSelectedFile = (fieldName, index = null) => {
    setFiles((prev) => {
      const currentValue = prev[fieldName];
      if (Array.isArray(currentValue)) {
        if (index === null) {
          return { ...prev, [fieldName]: [] };
        }
        return {
          ...prev,
          [fieldName]: currentValue.filter(
            (_, fileIndex) => fileIndex !== index,
          ),
        };
      }

      return { ...prev, [fieldName]: null };
    });
  };

  const renderSelectedUploadFiles = (fieldName) => {
    const currentValue = files[fieldName];

    if (Array.isArray(currentValue)) {
      if (!currentValue.length) return null;

      return (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {currentValue.map((file, index) => (
            <div key={`${file.name}-${index}`} className="selected-file-chip">
              <span className="selected-file-name">{file.name}</span>
              <Button
                type="button"
                variant="outline-danger"
                size="sm"
                className="ms-2"
                onClick={() => clearSelectedFile(fieldName, index)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      );
    }

    if (!currentValue) return null;

    return (
      <div className="d-flex flex-wrap gap-2 mb-2">
        <div className="selected-file-chip">
          <span className="selected-file-name">{currentValue.name}</span>
          <Button
            type="button"
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={() => clearSelectedFile(fieldName)}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      emp_name: "",
      education_qualification: "",
      certificate_reward: "",
      designation: "",
      work_experience: "",
      technical_skills: "",
      other_skills: "",
      email: "",
      job_location: "",
      address: "",
      firm_name: "Brainrock Consulting Services",
      govt_doc_type: "aadhaar",
      other_govt_doc_type: "",
    });
    setFiles({
      profile_pic: null,
      govt_document: null,
      educational_documents: [],
      experience_certificates: [],
      professional_certificates: [],
    });
    setEditMode(false);
    setExistingFiles({
      profile_pic: null,
      govt_document: null,
      educational_documents: [],
      experience_certificates: [],
      professional_certificates: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowAlert(false);

    const payload = new FormData();
    // Append form data
    for (const key in formData) {
      payload.append(key, formData[key]);
    }

    // Append files
    if (files.profile_pic) payload.append("profile_pic", files.profile_pic);
    if (files.govt_document)
      payload.append("govt_document", files.govt_document);
    files.educational_documents.forEach((file) =>
      payload.append("educational_documents", file),
    );
    files.experience_certificates.forEach((file) =>
      payload.append("experience_certificates", file),
    );
    files.professional_certificates.forEach((file) =>
      payload.append("professional_certificates", file),
    );

    try {
      let response;
      if (editMode && selectedEmployee) {
        payload.append("id", selectedEmployee.id);
        response = await axios.put(API_URL, payload, {
          withCredentials: true,
        });
        setMessage("Employee updated successfully!");
        setActiveTab("manage"); // Redirect to manage tab on successful update
      } else {
        response = await axios.post(API_URL, payload, {
          withCredentials: true,
        });
        setMessage("Employee added successfully!");
      }

      setVariant("success");
      setShowAlert(true);
      resetForm();
      fetchEmployees(); // Refresh the list
    } catch (error) {
      console.error("Submission error:", error.response?.data || error.message);
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message ||
        "An error occurred.";
      setMessage(backendMessage);
      setVariant("danger");
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleView = (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    setEditMode(true);
    setSelectedEmployee(employee);
    setFormData({
      emp_name: employee.emp_name,
      education_qualification: employee.education_qualification,
      certificate_reward: employee.certificate_reward,
      designation: employee.designation,
      work_experience: employee.work_experience,
      technical_skills: employee.technical_skills,
      other_skills: employee.other_skills,
      email: employee.email,
      job_location: employee.job_location,
      address: employee.address,
      firm_name: employee.firm_name || "Brainrock Consulting Services",
      govt_doc_type: employee.govt_doc_type,
      other_govt_doc_type: employee.other_govt_doc_type || "",
    });
    setExistingFiles({
      profile_pic: employee.profile_pic,
      govt_document: employee.govt_document,
      educational_documents: employee.educational_documents || [],
      experience_certificates: employee.experience_certificates || [],
      professional_certificates: employee.professional_certificates || [],
    });
    // Switch to the "Add/Edit" tab to show the form
    setActiveTab("add");
  };

  const handleDelete = async (id, firmName) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        await axios.delete(API_URL, {
          data: { id, firm_name: firmName },
          withCredentials: true,
        });
        setMessage("Employee deleted successfully.");
        setVariant("success");
        setShowAlert(true);
        fetchEmployees();
      } catch (error) {
        console.error("Delete error:", error);
        setMessage("Failed to delete employee.");
        setVariant("danger");
        setShowAlert(true);
      }
    }
  };

  const handleDeleteDoc = async (employeeId, field, index) => {
    if (window.confirm(`Are you sure you want to delete this document?`)) {
      try {
        await axios.delete(
          `${DOC_DELETE_URL}?id=${employeeId}&field=${field}&index=${index}`,
          {
            withCredentials: true,
          },
        );
        setMessage("Document deleted successfully.");
        setVariant("success");
        setShowAlert(true);
        fetchEmployees(); // Refresh data
        setShowModal(false); // Close modal
      } catch (error) {
        console.error("Doc delete error:", error);
        setMessage("Failed to delete document.");
        setVariant("danger");
        setShowAlert(true);
      }
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const generateEmployeePdfContent = (employee) => {
    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const firmName = (employee.firm_name || "Employee").trim();
    const firmsForBlackTheme = [
      "ZEE - Zero Error Enterprises",
      "Diksha Enterprises",
      "U.S. Infotech", // Added to use the black theme
    ];

    const shouldUseBlackTheme = firmsForBlackTheme.includes(firmName);
    const isZeeEmployee = firmName === "ZEE - Zero Error Enterprises";
    const isDikshaEmployee = firmName === "Diksha Enterprises";
    const isUSInfotechEmployee = firmName === "U.S. Infotech";
    const isBrainrockEmployee = firmName === "Brainrock Consulting Services";
    const accentColor = shouldUseBlackTheme
      ? "#000000"
      : isZeeEmployee
        ? "#c1121f"
        : "#0b3d91";
    const borderColor = shouldUseBlackTheme
      ? "#000000"
      : isZeeEmployee
        ? "#f2b8bf"
        : "#d7e1f0";
    const keyBgColor = shouldUseBlackTheme
      ? "#f5f5f5"
      : isZeeEmployee
        ? "#fff3f4"
        : "#f4f8ff";

    let pdfTitle;
    if (isDikshaEmployee) {
      pdfTitle = "Diksha Enterprises Employee Details";
    } else if (isUSInfotechEmployee) {
      pdfTitle = "U.S. Infotech Employee Details";
    } else {
      pdfTitle = `${firmName} Employee Details`;
    }

    const logoHtml = isBrainrockEmployee
      ? `<img src="${BrainRockLogo}" alt="BrainRock Logo" style="width: 80px; height: 80px; object-fit: contain;" />`
      : isZeeEmployee
        ? `<img src="${ZeeLogo}" alt="ZEE Logo" style="width: 80px; height: 80px; object-fit: contain;" />`
        : ""; // No logo for other firms

    // Define specific styles for body profile pictures
    const bodyProfilePicStyle = `width: 100px; height: 100px; object-fit: cover; border-radius: 50%; border: 3px solid ${borderColor};`;
    const defaultHeaderProfilePicStyle = isZeeEmployee
      ? "width: 60px; height: 60px; object-fit: cover; border-radius: 8px; border: 3px solid #e6edf7;"
      : "width: 60px; height: 60px; object-fit: cover; border-radius: 50%; border: 3px solid #e6edf7;";

    const profilePicHtml = employee.profile_pic
      ? `<img src="${DOC_BASE_URL}${employee.profile_pic}" alt="Profile Picture" style="${isDikshaEmployee || isUSInfotechEmployee ? bodyProfilePicStyle : defaultHeaderProfilePicStyle}" />`
      : `<div style="width: 60px; height: 60px; border-radius: ${isZeeEmployee ? "8px" : "50%"}; background-color: #e0e0e0; display: inline-flex; align-items: center; justify-content: center; color: #777; font-weight: bold; font-size: 10px; border: 3px solid ${shouldUseBlackTheme ? "#000000" : "#e6edf7"};">Locked</div>`;

    const renderDocPreview = (docPath, title) => {
      if (!docPath)
        return `<tr><td class="key">${title}</td><td>Document Locked</td></tr>`;
      const fullUrl = `${DOC_BASE_URL}${docPath}`;
      const fileName = docPath.split("/").pop();
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(docPath);

      if (isImage) {
        return `<tr><td class="key">${title}</td><td><img src="${fullUrl}" alt="${title}" class="doc-image" /></td></tr>`;
      }

      return `<tr><td class="key">${title}</td><td><a href="${fullUrl}" target="_blank">${escapeHtml(fileName)}</a></td></tr>`;
    };

    const renderDocListHtmlForPdf = (docs, title) => {
      if (!docs || docs.length === 0) {
        return `<tr><td class="key">${title}</td><td>Document Locked</td></tr>`;
      }

      const rows = docs
        .map((doc) => {
          const fullUrl = `${DOC_BASE_URL}${doc}`;
          const fileName = doc.split("/").pop();
          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc);

          if (isImage) {
            return `<div class="doc-card"><img src="${fullUrl}" alt="${title}" class="doc-image" /></div>`;
          }

          return `<div class="doc-card"><a href="${fullUrl}" target="_blank">${escapeHtml(fileName)}</a></div>`;
        })
        .join("");

      return `
        <tr>
          <td class="key">${title}</td>
          <td>
            <div class="doc-list-wrap">${rows}</div>
          </td>
        </tr>
      `;
    };

    const allProfileRows = [
      ["Employee ID", employee.emp_id || "N/A"],
      ["Name", employee.emp_name || "N/A"],
      ["Email", employee.email || "N/A"],
      ["Designation", employee.designation || "N/A"],
      ["Firm Name", employee.firm_name || "N/A"],
      ["Job Location", employee.job_location || "N/A"],
      ["Address", employee.address || "N/A"],
      ["Education Qualification", employee.education_qualification || "N/A"],
      ["Work Experience", employee.work_experience || "N/A"],
      ["Certificates / Rewards", employee.certificate_reward || "N/A"],
      ["Technical Skills", employee.technical_skills || "N/A"],
      ["Other Skills", employee.other_skills || "N/A"],
    ];

    let mainTableRows = allProfileRows;
    let specialLayoutEmpIdHtml = "";

    if (isDikshaEmployee) {
      // Only apply special Emp ID logic for Diksha
      // Extract Employee ID for separate display
      const empIdRowIndex = allProfileRows.findIndex(
        (row) => row[0] === "Employee ID",
      );
      if (empIdRowIndex !== -1) {
        const empIdRow = allProfileRows[empIdRowIndex];
        specialLayoutEmpIdHtml = `<p style="margin-top: 10px; font-weight: bold; font-size: 12px; color: ${accentColor};">Emp ID: ${escapeHtml(empIdRow[1])}</p>`;
        mainTableRows = allProfileRows.filter(
          (row, index) => index !== empIdRowIndex,
        );
      }
    }

    const tableMarkup = mainTableRows
      .map(
        ([label, value]) => `
          <tr>
            <td class="key">${escapeHtml(label)}</td>
            <td>${escapeHtml(value)}</td>
          </tr>
        `,
      )
      .join("");

    return `
      <html>
      <head>
          <title>Employee Details - ${escapeHtml(employee.emp_name || "Employee")}</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 16px;
                  font-size: 12px;
                  background: ${shouldUseBlackTheme ? "#FFFFFF" : "#ffffff"};
                  color: ${shouldUseBlackTheme ? "#000000" : "#1f2937"};
              }
              .pdf-shell {
                  max-width: 900px;
                  margin: 40px auto;
                  background: ${shouldUseBlackTheme ? "#FFFFFF" : "#ffffff"};
                  border: 1px solid ${borderColor};
                  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
                  border-radius: 12px;
                  overflow: hidden;
              }
              .pdf-header {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 8px 28px;
                  background: ${shouldUseBlackTheme ? "#FFFFFF" : "#ffffff"};
              }
              .pdf-brand {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  flex: 1;
              }
              .header-actions {
                  display: flex;
                  align-items: center;
                  justify-content: flex-end;
                  gap: 12px;
                  text-align: right;
                  flex: 1;
              }
              .profile-pic-header {
                  width: 60px;
                  height: 60px;
                  border-radius: ${isZeeEmployee ? "8px" : "50%"}; /* Diksha photo is not in header */
                  object-fit: cover;
                  border: 3px solid #e6edf7;
              }
              .profile-pic-placeholder-header {
                  width: 60px; height: 60px; border-radius: ${isZeeEmployee ? "8px" : "50%"}; background-color: #e0e0e0; /* Diksha photo is not in header */
                  display: inline-flex; align-items: center; justify-content: center;
                  color: #777; font-weight: bold; font-size: 10px;
                  border: 3px solid ${shouldUseBlackTheme ? "#000000" : "#e6edf7"};
              }
              .pdf-brand img {
                  width: 60px;
                  height: 60px;
                  background: #ffffff;
                  padding: 4px;
              }
              .pdf-header-center {
                  flex: 2;
                  text-align: center;
              }
                  .pdf-header-center h1{
                  margin-top:30px;
                  }
              .pdf-title {
                  margin: 0;
                  font-size: 20px;
                  color: ${accentColor};
                  font-weight: 700;
              }
              .pdf-subtitle {
                  margin: 4px 0 0;
                  font-size: 10px;
                  color: ${shouldUseBlackTheme ? "#000000" : "#555"};
              }
              .pdf-body {
                  padding: 24px 28px 28px;
              }
              .section-title {
                  margin: 0 0 12px;
                  font-size: 14px;
                  color: ${accentColor};
                  font-weight: 700;
                  border-bottom: 2px solid ${borderColor};
                  padding-bottom: 8px;
              }
              table {
                  width: 100%;
                  border-collapse: collapse;
                  table-layout: fixed;
              }
              td {
                  border: 1px solid ${borderColor};
                  padding: 10px 12px;
                  vertical-align: top;                  font-size: 12px;
                  word-wrap: break-word;
              }
              .key {
                  width: 32%;
                  background: ${keyBgColor};
                  font-weight: 700;
                  color: ${accentColor};
              }
              .doc-list-wrap {
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
              }
              .doc-card {
                  border: 1px solid ${borderColor};
                  border-radius: 6px;
                  padding: 8px;
                  background: ${keyBgColor};
              }
              .doc-image {
                  max-width: 100%;
                  max-height: 200px;
                  display: block;
                  margin: 0 auto;
                  border: 1px solid ${borderColor};
                  border-radius: 6px;
                  background: #ffffff;
              }
              a {
                  color: ${accentColor};
                  text-decoration: none;
              }
              .print-btn {
                  background-color: #ffffff;
                  color: #0b3d91;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 6px;
                  font-weight: 700;
                  cursor: pointer;
                  margin-top: 8px;
                  transition: background-color 0.2s;
              }
              .print-btn:hover {
                  background-color: #f0f0f0;
              }
              @media print {
                  body { background: #ffffff; padding: 0; }
                  .pdf-shell { box-shadow: none; border: none; }
                  .print-btn { display: none; }
              }
          </style>
      </head>
      <body>
          <div class="pdf-shell">
              <div class="pdf-header">
                  <div class="pdf-brand">
                      ${
                        isZeeEmployee
                          ? `<img src="${ZeeLogo}" alt="ZEE Logo" style="width: 80px; height: 80px; object-fit: contain;" /><p class="pdf-subtitle" style="margin-top: 5px;"> ${escapeHtml(employee.firm_name || "ZEE - Zero Error Enterprises")}</p>`
                          : isBrainrockEmployee
                            ? `<img src="${BrainRockLogo}" alt="BrainRock Logo" style="width: 80px; height: 80px; object-fit: contain;" /><p class="pdf-subtitle" style="margin-top: 5px;"> ${escapeHtml(employee.firm_name || "Brainrock Consulting Services")}</p>`
                            : `<p class="pdf-subtitle" style="margin-top: 5px;"> ${escapeHtml(employee.firm_name || "Employee")}</p>`
                      }
                  </div>
                  <div class="pdf-header-center">
                      <h1 class="pdf-title">${pdfTitle}</h1>
                  </div>
                  <div class="header-actions">
                      <div>
                          <div class="pdf-subtitle">Generated for ${escapeHtml(employee.emp_name || "Employee")}</div>
                          <button class="print-btn" onclick="window.print()">Print Report</button>
                      </div>
                      ${
                        !isDikshaEmployee // Show header photo for everyone except Diksha
                          ? employee.profile_pic
                            ? `<img src="${DOC_BASE_URL}${employee.profile_pic}" alt="Profile Picture" class="profile-pic-header" />`
                            : '<div class="profile-pic-placeholder-header">Locked</div>'
                          : ""
                      }
                  </div>
              </div>
              <div class="pdf-body">
                  ${
                    isUSInfotechEmployee
                      ? `
                    <div style="display: flex; gap: 24px; align-items: flex-start;">
                      <!-- Left Column: Profile Info -->
                      <div style="flex: 1.5;">
                        <h2 class="section-title">Profile Information</h2>
                        <table>
                          ${tableMarkup}
                        </table>
                      </div>
                      <!-- Right Column: Documents -->
                      <div style="flex: 1;">
                        <h2 class="section-title">Documents</h2>
                        <table>
                          ${renderDocPreview(employee.govt_document, `${escapeHtml(employee.govt_doc_type ? employee.govt_doc_type.toUpperCase() : "GOVT")} Document`)}
                          ${renderDocListHtmlForPdf(employee.educational_documents, "Educational Documents")}
                          ${renderDocListHtmlForPdf(employee.experience_certificates, "Experience Certificates")}
                          ${renderDocListHtmlForPdf(employee.professional_certificates, "Professional Certificates")}
                        </table>
                      </div>
                    </div>
                  `
                      : `
                    <h2 class="section-title">Profile Information</h2>
                    ${
                      isDikshaEmployee
                        ? `
                      <div style="display: flex; gap: 20px; align-items: flex-start;">
                        <div style="flex: 1;"><table>${tableMarkup}</table></div>
                        <div style="flex: 0 0 120px; text-align: center;">${profilePicHtml}${specialLayoutEmpIdHtml}</div>
                      </div>
                      <table>`
                        : `<table>${tableMarkup}`
                    }
                      ${renderDocPreview(employee.govt_document, `${escapeHtml(employee.govt_doc_type ? employee.govt_doc_type.toUpperCase() : "GOVT")} Document`)}
                      ${renderDocListHtmlForPdf(employee.educational_documents, "Educational Documents")}
                      ${renderDocListHtmlForPdf(employee.experience_certificates, "Experience Certificates")}
                      ${renderDocListHtmlForPdf(employee.professional_certificates, "Professional Certificates")}
                    </table>
                  `
                  }
              </div>
          </div>
      </body>
      </html>
    `;
  };

  const handleDownloadPdf = (employee) => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(generateEmployeePdfContent(employee));
    printWindow.document.close();
    printWindow.focus();
  };

  const uniqueFirmNames = ["all", ...FIRM_OPTIONS];

  const filteredEmployees =
    selectedFirmFilter === "all"
      ? allEmployees
      : allEmployees.filter((emp) => emp.firm_name === selectedFirmFilter);

  const renderDocumentListWithDelete = (docs, fieldName) => {
    if (!docs || docs.length === 0) return <p>Document Locked</p>;
    return (
      <ul>
        {docs.map((doc, index) => (
          <li
            key={index}
            className="d-flex justify-content-between align-items-center"
          >
            {renderDocumentPreviewInModal(doc)}
            <Button
              variant="danger"
              size="sm"
              onClick={() =>
                handleDeleteDoc(selectedEmployee.id, fieldName, index)
              }
            >
              Delete
            </Button>
          </li>
        ))}
      </ul>
    );
  };

  const renderFilePreview = (filePath) => {
    if (!filePath) return null;
    const fullUrl = `https://brainrock.in/brainrock/backend${filePath}`;
    const isImage = /\.(jpg|jpeg|png|gif)$/i.test(filePath);

    return (
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="file-preview-box"
      >
        {isImage ? (
          <img src={fullUrl} alt="preview" className="file-preview-image" />
        ) : (
          <div className="file-preview-icon">📄</div>
        )}
      </a>
    );
  };

  const generateFirmWisePdfContent = (firmName, employees) => {
    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const firmDisplayName = firmName === "all" ? "All Firms" : firmName;
    const isZeeEmployee = firmName === "ZEE - Zero Error Enterprises";
    const isDikshaEmployee = firmName === "Diksha Enterprises";
    const isUSInfotechEmployee = firmName === "U.S. Infotech";
    const isBrainrockEmployee = firmName === "Brainrock Consulting Services";

    const shouldUseBlackTheme = isDikshaEmployee || isUSInfotechEmployee;
    const accentColor = shouldUseBlackTheme
      ? "#000000"
      : isZeeEmployee
        ? "#c1121f"
        : "#0b3d91";
    const borderColor = shouldUseBlackTheme
      ? "#000000"
      : isZeeEmployee
        ? "#f2b8bf"
        : "#d7e1f0";
    const keyBgColor = shouldUseBlackTheme
      ? "#f5f5f5"
      : isZeeEmployee
        ? "#fff3f4"
        : "#f4f8ff";

    const employeeRows = employees
      .map(
        (emp, index) => `
        <tr>
          <td class="key">${index + 1}</td>
          <td>${escapeHtml(emp.emp_id || "N/A")}</td>
          <td>${escapeHtml(emp.emp_name || "N/A")}</td>
          <td>${escapeHtml(emp.work_experience || "N/A")}</td>
          <td>${escapeHtml(emp.designation || "N/A")}</td>
        </tr>
      `,
      )
      .join("");

    return `
      <html>
      <head>
          <title>${firmDisplayName} - Employee Report</title>
          <style>
              body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 16px;
                  font-size: 12px;
                  background: #ffffff;
                  color: ${shouldUseBlackTheme ? "#000000" : "#1f2937"};
              }
              .pdf-shell {
                  max-width: 1200px;
                  margin: 40px auto;
                  background: #ffffff;
                  border: 1px solid ${borderColor};
                  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
                  border-radius: 12px;
                  overflow: hidden;
              }
              .pdf-header {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 16px 28px;
                  background: #ffffff;
                  gap: 12px;
              }
              .pdf-brand {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  flex: 1;
              }
              .pdf-header-center {
                  flex: 2;
                  text-align: center;
              }
              .pdf-header-center .section-title {
                  margin: 0;
                  font-size: 16px;
                  color: ${accentColor};
                  font-weight: 700;
                  border-bottom: none;
                  padding-bottom: 0;
                  text-align: center;
              }
              .pdf-brand img {
                  width: 60px;
                  height: 60px;
                  background: #ffffff;
                  padding: 4px;
              }
              .pdf-title {
                  margin: 0;
                  font-size: 20px;
                  color: ${accentColor};
                  font-weight: 700;
              }
              .pdf-subtitle {
                  margin: 4px 0 0;
                  font-size: 10px;
                  color: ${shouldUseBlackTheme ? "#000000" : "#555"};
              }
              .pdf-body {
                  padding: 24px 28px 28px;
              }
              .section-title {
                  margin: 0 0 12px;
                  font-size: 14px;
                  color: ${accentColor};
                  font-weight: 700;
                  border-bottom: 2px solid ${borderColor};
                  padding-bottom: 8px;
                  text-align: center;
              }
              table {
                  width: 100%;
                  border-collapse: collapse;
                  table-layout: fixed;
              }
              td, th {
                  border: 1px solid ${borderColor};
                  padding: 10px 12px;
                  vertical-align: top;
                  font-size: 12px;
                  word-wrap: break-word;
              }
              th {
                  background: ${keyBgColor};
                  font-weight: 700;
                  color: ${accentColor};
              }
              .print-btn {
                  background-color: #ffffff;
                  color: #0b3d91;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 6px;
                  font-weight: 700;
                  cursor: pointer;
                  margin-top: 8px;
                  transition: background-color 0.2s;
              }
              .print-btn:hover {
                  background-color: #f0f0f0;
              }
              @media print {
                  body { background: #ffffff; padding: 0; }
                  .pdf-shell { box-shadow: none; border: none; }
                  .print-btn { display: none; }
              }
          </style>
      </head>
      <body>
          <div class="pdf-shell">
               <div class="pdf-header">
                    <div class="pdf-brand">
                        ${
                          isZeeEmployee
                            ? `<div style="display: flex; align-items: center; gap: 10px;"><img src="${ZeeLogo}" alt="ZEE Logo" style="width: 80px; height: 80px; object-fit: contain;" /><p class="pdf-subtitle" style="margin-top: 0;"> ${escapeHtml(firmName)}</p></div>`
                            : isBrainrockEmployee
                              ? `<div style="display: flex; align-items: center; gap: 10px;"><img src="${BrainRockLogo}" alt="BrainRock Logo" style="width: 80px; height: 80px; object-fit: contain;" /><p class="pdf-subtitle" style="margin-top: 0;"> ${escapeHtml(firmName)}</p></div>`
                              : `<p class="pdf-subtitle" style="margin-top: 0;"> ${escapeHtml(firmDisplayName)}</p>`
                        }
                    </div>
                   <div class="pdf-header-center">
                       <h2 class="section-title">Employee List - ${escapeHtml(firmDisplayName)}</h2>
                   </div>
                   <div style="display: flex; align-items: center; gap: 12px;">
                       <div class="pdf-subtitle">Generated on ${new Date().toLocaleDateString()} | Total Employees: ${employees.length}</div>
                       <button class="print-btn" onclick="window.print()">Print Report</button>
                   </div>
               </div>
               <div class="pdf-body">
                   <table>
                      <thead>
                           <tr>
                               <th style="width: 10%;">#</th>
                               <th style="width: 15%;">Emp ID</th>
                               <th style="width: 20%;">Name</th>
                               <th style="width: 25%;">Work Experience</th>
                               <th style="width: 30%;">Designation</th>
                           </tr>
                      </thead>
                      <tbody>
                          ${employeeRows}
                      </tbody>
                  </table>
              </div>
          </div>
      </body>
      </html>
    `;
  };

  const handleFirmWisePdf = () => {
    const employeesToUse =
      selectedFirmFilter === "all" ? allEmployees : filteredEmployees;

    if (employeesToUse.length === 0) {
      setMessage("No employees found for the selected firm.");
      setVariant("danger");
      setShowAlert(true);
      return;
    }

    const htmlContent = generateFirmWisePdfContent(
      selectedFirmFilter,
      employeesToUse,
    );
    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleFirmWiseExcel = () => {
    const employeesToUse =
      selectedFirmFilter === "all" ? allEmployees : filteredEmployees;

    if (employeesToUse.length === 0) {
      setMessage("No employees found for the selected firm.");
      setVariant("danger");
      setShowAlert(true);
      return;
    }

    const excelData = employeesToUse.map((emp, index) => ({
      "S.No": index + 1,
      "Emp ID": emp.emp_id || "",
      Name: emp.emp_name || "",
      "Work Experience": emp.work_experience || "",
      Designation: emp.designation || "",
      "Firm Name": emp.firm_name || "",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");

    const firmName =
      selectedFirmFilter === "all"
        ? "All_Firms"
        : selectedFirmFilter.replace(/[^a-zA-Z0-9]/g, "_");
    XLSX.writeFile(
      wb,
      `${firmName}_Employees_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handleFirmWisePrint = () => {
    const employeesToUse =
      selectedFirmFilter === "all" ? allEmployees : filteredEmployees;

    if (employeesToUse.length === 0) {
      setMessage("No employees found for the selected firm.");
      setVariant("danger");
      setShowAlert(true);
      return;
    }

    const htmlContent = generateFirmWisePdfContent(
      selectedFirmFilter,
      employeesToUse,
    );
    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };


  // Generate one combined document containing the COMPLETE details of
  // every employee. Each employee starts on a new printable page.
  const generateAllEmployeesPdfContent = (employeesList) => {
    if (!employeesList || employeesList.length === 0) return "";

    const firstEmployeeHtml = generateEmployeePdfContent(employeesList[0]);

    // Reuse the same styling already used by the individual employee PDF.
    const styleStart = firstEmployeeHtml.indexOf("<style>");
    const styleEnd = firstEmployeeHtml.indexOf("</style>");
    const sharedStyles =
      styleStart !== -1 && styleEnd !== -1
        ? firstEmployeeHtml.slice(styleStart + 7, styleEnd)
        : "";

    const employeePages = employeesList
      .map((employee, index) => {
        const employeeHtml = generateEmployeePdfContent(employee);
        const bodyStart = employeeHtml.indexOf("<body>");
        const bodyEnd = employeeHtml.indexOf("</body>");
        const bodyContent =
          bodyStart !== -1 && bodyEnd !== -1
            ? employeeHtml.slice(bodyStart + 6, bodyEnd)
            : employeeHtml;

        return `
          <div class="combined-employee-page" data-employee-index="${index}">
            ${bodyContent}
          </div>
        `;
      })
      .join("");

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\\"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const firmNames = [
      ...new Set(
        employeesList
          .map((employee) => employee.firm_name)
          .filter(Boolean),
      ),
    ];

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8" />
        <title>All Employees - Employee Details</title>
        <style>
          ${sharedStyles}

          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
          }

          .combined-employee-page {
            width: 100%;
            page-break-after: always;
            break-after: page;
            padding: 0;
            margin: 0;
          }

          .combined-employee-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          .combined-employee-page .pdf-shell {
            margin: 20px auto;
            box-shadow: none;
          }

          .combined-summary {
            max-width: 900px;
            margin: 20px auto 0;
            padding: 12px 20px;
            border: 1px solid #d7e1f0;
            border-radius: 8px;
            font-family: Arial, sans-serif;
            font-size: 12px;
            background: #f8fbff;
          }

          @media print {
            .combined-summary {
              display: none;
            }

            .combined-employee-page {
              page-break-after: always;
              break-after: page;
            }

            .combined-employee-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }

            .combined-employee-page .pdf-shell {
              margin: 0 auto;
              border-radius: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="combined-summary">
          <strong>All Employee Details</strong><br />
          Total Employees: ${employeesList.length}<br />
          Firms Included: ${escapeHtml(firmNames.join(", ") || "All Firms")}<br />
          Generated on: ${escapeHtml(new Date().toLocaleDateString())}
        </div>
        ${employeePages}
      </body>
      </html>
    `;
  };

  // Employees used by the bulk View / PDF actions.
  // This works for both "All Firms" and any single selected firm.
  const getBulkEmployees = () => {
    if (selectedFirmFilter === "all") {
      return Array.isArray(allEmployees) ? allEmployees : [];
    }

    return Array.isArray(filteredEmployees) ? filteredEmployees : [];
  };

  const handleViewAllEmployeesPdf = () => {
    const employeesToUse = getBulkEmployees();

    if (employeesToUse.length === 0) {
      setMessage("No employees found.");
      setVariant("danger");
      setShowAlert(true);
      return;
    }

    const htmlContent = generateAllEmployeesPdfContent(employeesToUse);
    setAllEmployeesPreviewHtml(htmlContent);
    setShowAllEmployeesModal(true);
  };

  const handleDownloadAllEmployeesPdf = () => {
    const employeesToUse = getBulkEmployees();

    if (employeesToUse.length === 0) {
      setMessage("No employees found.");
      setVariant("danger");
      setShowAlert(true);
      return;
    }

    const htmlContent = generateAllEmployeesPdfContent(employeesToUse);
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      setMessage("Please allow pop-ups in your browser to download the PDF.");
      setVariant("danger");
      setShowAlert(true);
      return;
    }

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <LeftNavManagement
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        <AdminHeader toggleSidebar={toggleSidebar} />

        {/* Dashboard Body */}
        <Container fluid className="dashboard-body ">
          <h2 className="mb-4">Employee Management</h2>
          <div className="br-box-container mt-3">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              id="employee-tabs"
              className="mb-3"
            >
              <Tab
                eventKey="add"
                title={editMode ? "Edit Employee" : "Add Employee"}
              >
                <h4 className="mb-4">
                  {editMode ? "Edit Employee Details" : "Add New Employee"}
                </h4>
                {showAlert && (
                  <Alert
                    variant={variant}
                    onClose={() => setShowAlert(false)}
                    dismissible
                  >
                    {message}
                  </Alert>
                )}
                <Form onSubmit={handleSubmit} className="p-3">
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Employee Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="emp_name"
                          value={formData.emp_name}
                          onChange={handleChange}
                          placeholder="Enter full name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Designation</Form.Label>
                        <Form.Control
                          type="text"
                          name="designation"
                          value={formData.designation}
                          onChange={handleChange}
                          placeholder="e.g., Software Engineer"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Job Location</Form.Label>
                        <Form.Control
                          type="text"
                          name="job_location"
                          value={formData.job_location}
                          onChange={handleChange}
                          placeholder="e.g., Dehradun"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Enter employee's current address"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Education Qualification</Form.Label>
                        <Form.Control
                          type="text"
                          name="education_qualification"
                          value={formData.education_qualification}
                          onChange={handleChange}
                          placeholder="e.g., MCA, B.Tech"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Work Experience</Form.Label>
                        <Form.Control
                          type="text"
                          name="work_experience"
                          value={formData.work_experience}
                          onChange={handleChange}
                          placeholder="e.g., 5 Years"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Certificate/Reward</Form.Label>
                        <Form.Control
                          type="text"
                          name="certificate_reward"
                          value={formData.certificate_reward}
                          onChange={handleChange}
                          placeholder="e.g., OCP, J2EE"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Technical Skills</Form.Label>
                        <textarea
                          className="form-control"
                          rows="4"
                          name="technical_skills"
                          value={formData.technical_skills}
                          onChange={handleChange}
                          placeholder="e.g., React.js, JavaScript, HTML, CSS, Node.js"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Other Skills</Form.Label>
                        <Form.Control
                          type="text"
                          name="other_skills"
                          value={formData.other_skills}
                          onChange={handleChange}
                          placeholder="e.g., Digital Marketing, SEO"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Firm Name</Form.Label>
                        <Form.Select
                          name="firm_name"
                          value={formData.firm_name}
                          onChange={handleChange}
                          required
                        >
                          {FIRM_OPTIONS.map((firm) => (
                            <option key={firm} value={firm}>
                              {firm}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Govt. Doc Type</Form.Label>
                        <Form.Select
                          name="govt_doc_type"
                          value={formData.govt_doc_type}
                          onChange={handleChange}
                        >
                          <option value="aadhaar">Aadhaar</option>
                          <option value="pan">PAN</option>
                          <option value="passport">Passport</option>
                          <option value="voter_id">Voter ID</option>
                          <option value="driving_license">
                            Driving License
                          </option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {formData.govt_doc_type === "other" && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Please specify other document type
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="other_govt_doc_type"
                            value={formData.other_govt_doc_type}
                            onChange={handleChange}
                            placeholder="Enter document type"
                            required
                          />
                        </Form.Group>
                      </Col>
                    )}

                    <h5 className="mt-4">Upload Documents</h5>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Profile Picture</Form.Label>
                        {editMode && existingFiles.profile_pic && (
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {renderFilePreview(existingFiles.profile_pic)}
                          </div>
                        )}
                        {renderSelectedUploadFiles("profile_pic")}
                        <Form.Control
                          type="file"
                          name="profile_pic"
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Government Document</Form.Label>
                        {editMode && existingFiles.govt_document && (
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {renderFilePreview(existingFiles.govt_document)}
                          </div>
                        )}
                        {renderSelectedUploadFiles("govt_document")}
                        <Form.Control
                          type="file"
                          name="govt_document"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Educational Documents</Form.Label>
                        {editMode &&
                          existingFiles.educational_documents.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {existingFiles.educational_documents.map(
                                (doc, i) => renderFilePreview(doc),
                              )}
                            </div>
                          )}
                        {renderSelectedUploadFiles("educational_documents")}
                        <Form.Control
                          type="file"
                          name="educational_documents"
                          onChange={handleFileChange}
                          multiple
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Experience Certificates</Form.Label>
                        {editMode &&
                          existingFiles.experience_certificates.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {existingFiles.experience_certificates.map(
                                (doc, i) => renderFilePreview(doc),
                              )}
                            </div>
                          )}
                        {renderSelectedUploadFiles("experience_certificates")}
                        <Form.Control
                          type="file"
                          name="experience_certificates"
                          onChange={handleFileChange}
                          multiple
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Professional Certificates</Form.Label>
                        {editMode &&
                          existingFiles.professional_certificates.length >
                            0 && (
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {existingFiles.professional_certificates.map(
                                (doc, i) => renderFilePreview(doc),
                              )}
                            </div>
                          )}
                        {renderSelectedUploadFiles("professional_certificates")}
                        <Form.Control
                          type="file"
                          name="professional_certificates"
                          onChange={handleFileChange}
                          multiple
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : editMode
                        ? "Update Employee"
                        : "Add Employee"}
                  </Button>
                  {editMode && (
                    <Button
                      variant="secondary"
                      className="ms-2"
                      onClick={resetForm}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </Form>
              </Tab>
              <Tab eventKey="manage" title="Manage Employees">
                <h4 className="mb-4">Existing Employees</h4>
                <Row className="mb-3 align-items-end">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Filter by Firm</Form.Label>
                      <Form.Select
                        value={selectedFirmFilter}
                        onChange={(e) => setSelectedFirmFilter(e.target.value)}
                      >
                        <option value="all">All Firms</option>
                        {uniqueFirmNames
                          .filter((firm) => firm !== "all")
                          .map((firm) => (
                            <option key={firm} value={firm}>
                              {firm}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={8} className="d-flex gap-2 align-items-end flex-wrap">
                    {/*
                      BULK COMPLETE-DETAIL ACTIONS
                      These buttons are intentionally ALWAYS visible.
                      - All Firms: allEmployees are used.
                      - Single Firm: filteredEmployees are used.
                    */}
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={handleViewAllEmployeesPdf}
                      disabled={getBulkEmployees().length === 0}
                      title={
                        selectedFirmFilter === "all"
                          ? "View complete details of all employees"
                          : `View complete details of all employees of ${selectedFirmFilter}`
                      }
                    >
                      <FaEye className="me-1" />
                      View All Details
                    </Button>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleDownloadAllEmployeesPdf}
                      disabled={getBulkEmployees().length === 0}
                      title={
                        selectedFirmFilter === "all"
                          ? "Download complete details of all employees in one PDF"
                          : `Download complete details of all employees of ${selectedFirmFilter} in one PDF`
                      }
                    >
                      <FaFilePdf className="me-1" />
                      Download All PDF
                    </Button>

                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleFirmWiseExcel}
                    >
                      Download Excel
                    </Button>

                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleFirmWisePdf}
                    >
                      Download Summary PDF
                    </Button>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleFirmWisePrint}
                    >
                      Print
                    </Button>
                  </Col>
                </Row>
                {loading ? (
                  <div className="text-center">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Emp ID</th>
                        <th>Name</th>
                        <th>Work Experience</th>
                        <th>Designation</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.map((emp, index) => (
                        <tr key={emp.id}>
                          <td>{index + 1}</td>
                          <td>{emp.emp_id}</td>
                          <td>{emp.emp_name}</td>
                          <td>{emp.work_experience}</td>
                          <td>{emp.designation}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleView(emp)}
                                title="View Details"
                              >
                                <FaEye />
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleDownloadPdf(emp)}
                                title="Download PDF"
                              >
                                <FaFilePdf />
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleEdit(emp)}
                                title="Edit Employee"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleDelete(emp.id, emp.firm_name)
                                }
                                title="Delete Employee"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>
            </Tabs>
          </div>
        </Container>
      </div>

      {/* All Employees Combined PDF Preview Modal */}
      <Modal
        show={showAllEmployeesModal}
        onHide={() => setShowAllEmployeesModal(false)}
        size="xl"
        fullscreen="lg-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            All Employees Details Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, background: "#f1f5f9" }}>
          <iframe
            title="All Employees Details Preview"
            srcDoc={allEmployeesPreviewHtml}
            style={{
              width: "100%",
              height: "75vh",
              border: "none",
              background: "#fff",
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <span className="me-auto text-muted small">
            {(selectedFirmFilter === "all" ? allEmployees : filteredEmployees).length} employee(s) included in this single PDF.
          </span>
          <Button
            variant="secondary"
            onClick={() => setShowAllEmployeesModal(false)}
          >
            Close
          </Button>
          <Button
            variant="danger"
            onClick={handleDownloadAllEmployeesPdf}
            disabled={
              (selectedFirmFilter === "all" ? allEmployees : filteredEmployees)
                .length === 0
            }
          >
            <FaFilePdf className="me-1" />
            {selectedFirmFilter === "all" ? "Download All PDF" : "Download Firm PDF"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Employee Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Employee Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployee && (
            <div>
              <div className="text-center mb-4">
                {selectedEmployee.profile_pic ? (
                  <img
                    src={`${DOC_BASE_URL}${selectedEmployee.profile_pic}`}
                    alt="Profile"
                    className="rounded-circle"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      border: "3px solid #eee",
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-light d-flex justify-content-center align-items-center"
                    style={{ width: "120px", height: "120px" }}
                  >
                    Locked
                  </div>
                )}
              </div>
              <Row>
                <Col md={6}>
                  <strong>Emp ID:</strong> <p>{selectedEmployee.emp_id}</p>
                </Col>
                <Col md={6}>
                  <strong>Name:</strong> <p>{selectedEmployee.emp_name}</p>
                </Col>
                <Col md={6}>
                  <strong>Email:</strong> <p>{selectedEmployee.email}</p>
                </Col>
                <Col md={6}>
                  <strong>Designation:</strong>{" "}
                  <p>{selectedEmployee.designation}</p>
                </Col>
                <Col md={6}>
                  <strong>Job Location:</strong>{" "}
                  <p>{selectedEmployee.job_location}</p>
                </Col>
                <Col md={6}>
                  <strong>Work Experience:</strong>{" "}
                  <p>{selectedEmployee.work_experience}</p>
                </Col>
                <Col md={12}>
                  <strong>Address:</strong> <p>{selectedEmployee.address}</p>
                </Col>
                <Col md={12}>
                  <strong>Education:</strong>{" "}
                  <p>{selectedEmployee.education_qualification}</p>
                </Col>
                <Col md={12}>
                  <strong>Certificates/Rewards:</strong>{" "}
                  <p>{selectedEmployee.certificate_reward}</p>
                </Col>
                <Col md={12}>
                  <strong>Technical Skills:</strong>{" "}
                  <p>{selectedEmployee.technical_skills}</p>
                </Col>
                <Col md={12}>
                  <strong>Other Skills:</strong>{" "}
                  <p>{selectedEmployee.other_skills}</p>
                </Col>
              </Row>
              <hr />
              <h4>Documents</h4>
              <Row>
                <Col md={12} className="mb-3">
                  <strong>
                    {selectedEmployee.govt_doc_type.toUpperCase()} Document:
                  </strong>
                  <div className="d-flex justify-content-between align-items-center">
                    {renderDocumentPreviewInModal(
                      selectedEmployee.govt_document,
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() =>
                        handleDeleteDoc(selectedEmployee.id, "govt_document", 0)
                      }
                    >
                      Delete
                    </Button>
                  </div>
                </Col>
                <Col md={12} className="mt-3">
                  <strong>Educational Documents:</strong>
                  {renderDocumentListWithDelete(
                    selectedEmployee.educational_documents,
                    "educational_documents",
                  )}
                </Col>
                <Col md={12} className="mt-3">
                  <strong>Experience Certificates:</strong>
                  {renderDocumentListWithDelete(
                    selectedEmployee.experience_certificates,
                    "experience_certificates",
                  )}
                </Col>
                <Col md={12} className="mt-3">
                  <strong>Professional Certificates:</strong>
                  {renderDocumentListWithDelete(
                    selectedEmployee.professional_certificates,
                    "professional_certificates",
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style type="text/css">
        {`
          .file-preview-box {
            display: inline-block;
            width: 80px;
            height: 80px;
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
            text-align: center;
            background-color: #f8f9fa;
          }
          .file-preview-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .file-preview-icon {
            font-size: 40px;
            line-height: 80px;
          }
          .file-preview-image-small {
              width: 60px;
              height: 60px;
              object-fit: cover;
              border: 1px solid #ddd;
              border-radius: 4px;
              background-color: #f8f9fa;
              margin-right: 5px;
          }
          .file-preview-link {
            display: inline-block;
            text-decoration: none;
            color: #007bff;
          }
          .selected-file-chip {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 10px;
            border: 1px solid #d9dee7;
            border-radius: 4px;
            background: #f8f9fa;
            margin-bottom: 8px;
            max-width: 100%;
          }
          .selected-file-name {
            display: inline-block;
            max-width: 220px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 12px;
          }
        `}
      </style>
    </div>
  );
};

export default EmployeeDetailsManagement;
